import { NOTE_PREFIX, NULLIFIER_BYTES, SECRET_BYTES } from "./constants";
import { createDeposit } from "./commitment";
const PRE_IMAGE_HEX_LEN = (NULLIFIER_BYTES + SECRET_BYTES) * 2; // 124 hex chars
/**
 * Note format: `tonado-<assetId>-<amount>-<network>-<hex>`
 * where `<hex>` is the 0x-prefixed pre-image bytes (62 bytes, 124 hex chars),
 * laid out as `nullifier_le ‖ secret_le`.
 *
 * `<assetId>` is one of:
 *  - `ton`
 *  - `jetton:<jetton-master-friendly-address>`
 *
 * Examples:
 *   tonado-ton-1-testnet-0x<124 hex chars>
 *   tonado-jetton:EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA-100-mainnet-0x<...>
 */
const HEX_RE = `0x[0-9a-fA-F]{${PRE_IMAGE_HEX_LEN}}`;
const NOTE_RE_GLOBAL = new RegExp(`^${NOTE_PREFIX}-([^-]+(?:-[^-]+)?)-(\\d+(?:\\.\\d+)?)-(mainnet|testnet)-(${HEX_RE})$`);
export function noteRegex() {
    return new RegExp(NOTE_RE_GLOBAL.source);
}
export function isValidNote(note) {
    return noteRegex().test(note);
}
export async function parseNote(note) {
    const m = note.match(noteRegex());
    if (!m) {
        throw new Error(`Invalid note format: ${truncate(note)}`);
    }
    const [, assetId, amount, network, hex] = m;
    const preImageHex = hex.slice(2).toLowerCase();
    const nullifierHex = preImageHex.slice(0, NULLIFIER_BYTES * 2);
    const secretHex = preImageHex.slice(NULLIFIER_BYTES * 2);
    // Hex is little-endian. Reverse to big-endian for BigInt.
    const nullifier = leHexToBigInt(nullifierHex);
    const secret = leHexToBigInt(secretHex);
    const deposit = await createDeposit({ nullifier, secret });
    return {
        assetId,
        amount,
        network: network,
        preImageHex,
        deposit,
    };
}
export function buildNote(opts) {
    const { assetId, amount, network, deposit } = opts;
    const hex = "0x" +
        bigintToLeHex(deposit.nullifier, NULLIFIER_BYTES) +
        bigintToLeHex(deposit.secret, SECRET_BYTES);
    return `${NOTE_PREFIX}-${assetId}-${amount}-${network}-${hex}`;
}
export async function generateNote(opts) {
    const deposit = await createDeposit();
    const note = buildNote({ ...opts, deposit });
    return { note, deposit };
}
/**
 * Helper used by relayers/UIs to embed a "request for funds" link.
 * The invoice is a shareable URL pointing at the deposit pool.
 */
export function createInvoice(opts) {
    const u = new URL(opts.baseUrl);
    u.searchParams.set("asset", opts.assetId);
    u.searchParams.set("amount", opts.amount);
    u.searchParams.set("network", opts.network);
    return u.toString();
}
function leHexToBigInt(hex) {
    // Reverse byte order: input is LE, BigInt wants BE.
    const bytes = hex.match(/.{2}/g) ?? [];
    return BigInt("0x" + bytes.reverse().join(""));
}
function bigintToLeHex(value, byteLen) {
    let hex = value.toString(16);
    if (hex.length % 2)
        hex = "0" + hex;
    const be = hex.padStart(byteLen * 2, "0");
    // Reverse bytes for LE
    const bytes = (be.match(/.{2}/g) ?? []).reverse();
    return bytes.join("");
}
function truncate(s) {
    return s.length > 40 ? s.slice(0, 40) + "…" : s;
}
//# sourceMappingURL=notes.js.map